import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PublicHolidaySchema, type PublicHolidayDto } from '@taqon/contracts';
import { z } from 'zod';
import { getData, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { messageOf } from './leave';

const HOLIDAYS = '/api/v1/hr/public-holidays';
const WEEKDAY = new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' });

function weekdayOf(date: string): string {
  return WEEKDAY.format(new Date(`${date}T00:00:00Z`));
}

/**
 * Public holidays (`/hr/public-holidays`; CLR-46, FRD-HRP-091): HR enters each holiday as it is
 * announced, and leave counted in days does not count it. A moved holiday changes its date; a
 * withdrawn one is made inactive, never deleted. Requests already submitted keep the duration
 * they were submitted with.
 */
export function PublicHolidaysPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'hr.public_holiday:create');
  const canChange = hasCapability(session.data, 'hr.public_holiday:change');
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const holidays = useQuery({
    queryKey: ['hr', 'public-holidays', year],
    queryFn: () => getData(`${HOLIDAYS}?year=${String(year)}`, z.array(PublicHolidaySchema)),
  });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PublicHolidayDto | null>(null);
  const [toggling, setToggling] = useState<PublicHolidayDto | null>(null);
  const rows = holidays.data ?? [];

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Public holidays"
        description="Announced public holidays. Leave counted in days does not count them."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(event) => {
                event.currentTarget.focus();
                setCreating(true);
              }}
            >
              Add holiday
            </Button>
          ) : undefined
        }
      />
      <Alert severity="info" variant="outlined">
        Add each holiday when it is announced. A request already submitted keeps its duration; a
        holiday added later is counted only in requests submitted or resubmitted after it.
      </Alert>
      <Section
        title="Holidays"
        actions={
          <TextField
            select
            size="small"
            label="Year"
            value={year}
            onChange={(e) => {
              setYear(Number(e.target.value));
            }}
            sx={{ minWidth: 120 }}
          >
            {[thisYear - 1, thisYear, thisYear + 1, thisYear + 2].map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </TextField>
        }
      >
        {holidays.isPending && <LoadingState label="Loading public holidays" />}
        {holidays.isError && <ErrorState message={holidays.error.message} />}
        {holidays.data !== undefined && rows.length === 0 && (
          <EmptyState
            title={`No public holidays for ${String(year)}`}
            {...(canCreate ? { detail: 'Add them as they are announced.' } : {})}
          />
        )}
        {rows.length > 0 && (
          <Table size="small" aria-label="Public holidays">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Day</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                {canChange && <TableCell align="right">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((h) => (
                <TableRow key={h.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {h.holidayDate}
                  </TableCell>
                  <TableCell>{weekdayOf(h.holidayDate)}</TableCell>
                  <TableCell>{h.name}</TableCell>
                  <TableCell>
                    <StatusChip status={h.status} />
                  </TableCell>
                  {canChange && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button
                        size="small"
                        aria-label={`Edit ${h.name}`}
                        onClick={(event) => {
                          event.currentTarget.focus();
                          setEditing(h);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color={h.status === 'active' ? 'warning' : 'primary'}
                        aria-label={`${h.status === 'active' ? 'Withdraw' : 'Restore'} ${h.name}`}
                        onClick={(event) => {
                          event.currentTarget.focus();
                          setToggling(h);
                        }}
                      >
                        {h.status === 'active' ? 'Withdraw' : 'Restore'}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      {(creating || editing !== null) && (
        <HolidayDialog
          {...(editing !== null ? { holiday: editing } : {})}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {toggling !== null && (
        <ToggleHoliday
          holiday={toggling}
          onClose={() => {
            setToggling(null);
          }}
        />
      )}
    </Stack>
  );
}

function HolidayDialog({ holiday, onClose }: { holiday?: PublicHolidayDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [date, setDate] = useState(holiday?.holidayDate ?? '');
  const [name, setName] = useState(holiday?.name ?? '');
  const save = useMutation({
    mutationFn: () =>
      holiday === undefined
        ? postJson(
            HOLIDAYS,
            { holidayDate: date, name: name.trim() },
            PublicHolidaySchema,
            session.data?.csrfToken ?? '',
          )
        : patchJson(
            `${HOLIDAYS}/${holiday.id}`,
            { holidayDate: date, name: name.trim() },
            PublicHolidaySchema,
            session.data?.csrfToken ?? '',
            holiday.version,
          ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'public-holidays'] });
      await queryClient.invalidateQueries({ queryKey: ['hr', 'leave', 'working-days'] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={holiday === undefined ? 'Add a public holiday' : `Edit ${holiday.name}`}
      submitLabel={holiday === undefined ? 'Add holiday' : 'Save changes'}
      canSubmit={date !== '' && name.trim() !== ''}
      submitting={save.isPending}
      error={save.isError ? messageOf(save.error) : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        required
        type="date"
        label="Date"
        value={date}
        onChange={(e) => {
          setDate(e.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        required
        label="Name"
        value={name}
        helperText="As announced, e.g. Eid Al Fitr"
        onChange={(e) => {
          setName(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 100 } }}
      />
    </FormDialog>
  );
}

function ToggleHoliday({ holiday, onClose }: { holiday: PublicHolidayDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const next = holiday.status === 'active' ? 'inactive' : 'active';
  const change = useMutation({
    mutationFn: () =>
      patchJson(
        `${HOLIDAYS}/${holiday.id}`,
        { status: next },
        PublicHolidaySchema,
        session.data?.csrfToken ?? '',
        holiday.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'public-holidays'] });
      await queryClient.invalidateQueries({ queryKey: ['hr', 'leave', 'working-days'] });
      onClose();
    },
  });
  return (
    <ConfirmDialog
      open
      title={`${next === 'inactive' ? 'Withdraw' : 'Restore'} ${holiday.name}?`}
      message={
        next === 'inactive'
          ? `${holiday.holidayDate} will count as a working day in new leave requests.`
          : `${holiday.holidayDate} will not count in new leave requests.`
      }
      confirmLabel={next === 'inactive' ? 'Withdraw' : 'Restore'}
      tone={next === 'inactive' ? 'warning' : 'primary'}
      busy={change.isPending}
      error={change.isError ? change.error.message : null}
      onCancel={onClose}
      onConfirm={() => {
        change.mutate();
      }}
    />
  );
}
